import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import Input from '@/components/ui/Input';
import PasswordInput from '@/components/ui/PasswordInput';
import Checkbox from '@/components/ui/Checkbox/Checkbox';
import Button from '@/components/ui/Button';
import Link from '@/components/ui/Link';
import Alert, { AlertType } from '@/components/ui/Alert';
import { PRIVACY_POLICY_URL, PRIVACY_POLICY_VERSION } from '@/data/privacyPolicy';
import styles from './AuthPages.module.scss';

const SigninComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser, signin } = useAuthContext();
  const [formData, setFormData] = useState({ email: '', password: '', firstName: '', lastName: '', phone: '' });
  const [privacyAcceptedAt, setPrivacyAcceptedAt] = useState<string | null>(null);
  const [errors, setErrors] = useState<{
    email?: string;
    password?: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    privacy?: string;
  }>({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [alertMessage, setAlertMessage] = useState<{ message: string; type: AlertType }>({ message: '', type: 'info' });
  const privacyCheckboxRef = useRef<HTMLInputElement>(null);

  // After submit without consent, focus the consent <input> once the error is painted.
  useEffect(() => {
    if (!submitted || !errors.privacy) return;
    const id = window.requestAnimationFrame(() => {
      privacyCheckboxRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(id);
  }, [submitted, errors.privacy]);

  const validateForm = () => {
    const newErrors: {
      email?: string;
      password?: string;
      firstName?: string;
      lastName?: string;
      phone?: string;
      privacy?: string;
    } = {};
    if (!formData.email) {
      newErrors.email = t('email') + ' ' + t('required');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('email') + ' ' + t('invalid');
    }
    if (!formData.password) {
      newErrors.password = t('password') + ' ' + t('required');
    }
    if (!formData.firstName) {
      newErrors.firstName = t('firstName') + ' ' + t('required');
    }
    if (!formData.lastName) {
      newErrors.lastName = t('lastName') + ' ' + t('required');
    }
    if (!formData.phone) {
      newErrors.phone = t('phoneNumber') + ' ' + t('required');
    } else if (!/^\d{10}$/.test(formData.phone)) {
      newErrors.phone = t('phoneNumber') + ' ' + t('invalid');
    }
    // SPR-1204: coordinator self-registration cannot submit without privacy consent.
    if (!privacyAcceptedAt) {
      newErrors.privacy = t('signupPrivacyConsent.error');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitted(true);
    setAlertMessage({ message: '', type: 'info' });

    if (!validateForm()) {
      // Focus is moved in an effect when errors.privacy is set — sync focus here is
      // unreliable after React re-renders the error state (RECRITIC R2).
      return;
    }

    setLoading(true);

    try {
      await signin(formData.email, formData.password, formData.firstName, formData.lastName, formData.phone, {
        accepted: true,
        version: PRIVACY_POLICY_VERSION,
        acceptedAt: privacyAcceptedAt!,
      });
      navigate('/login');
    } catch (error: unknown) {
      setAlertMessage({ message: (error as Error).message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.formContainer}>
      {!currentUser && (
        <form onSubmit={handleSubmit} className={styles.form} aria-labelledby="signup-heading">
          {/*
            כותרת h1 לעמוד. עמוד ההרשמה הוא נקודת כניסה ציבורית, וללא כותרת
            ראשית קורא מסך לא מקבל מושג היכן הוא נמצא. sr-only כדי לא לשנות
            את העיצוב הקיים.
          */}
          <h1 id="signup-heading" className="sr-only">
            {t('signup')}
          </h1>

          {alertMessage.message && <Alert type={alertMessage.type} message={alertMessage.message} />}

          <div className={styles.field}>
            <Input
              type="email"
              name="email"
              placeholder={t('email')}
              aria-label={t('email')}
              value={formData.email}
              onChange={handleChange}
              error={submitted && !!errors.email}
              autoComplete="email"
              autoFocus
              aria-describedby={submitted && errors.email ? 'signup-email-error' : undefined}
            />
            {submitted && errors.email && (
              <p id="signup-email-error" role="alert" className={`${styles.fieldError} body-sm-regular`}>
                {errors.email}
              </p>
            )}
          </div>

          <div className={styles.field}>
            <Input
              type="text"
              name="firstName"
              placeholder={t('firstName')}
              aria-label={t('firstName')}
              value={formData.firstName}
              onChange={handleChange}
              autoComplete="given-name"
              aria-describedby={submitted && errors.firstName ? 'signup-firstName-error' : undefined}
            />
            {submitted && errors.firstName && (
              <p id="signup-firstName-error" role="alert" className={`${styles.fieldError} body-sm-regular`}>
                {errors.firstName}
              </p>
            )}
          </div>

          <div className={styles.field}>
            <Input
              type="text"
              name="lastName"
              placeholder={t('lastName')}
              aria-label={t('lastName')}
              value={formData.lastName}
              onChange={handleChange}
              autoComplete="family-name"
              aria-describedby={submitted && errors.lastName ? 'signup-lastName-error' : undefined}
            />
            {submitted && errors.lastName && (
              <p id="signup-lastName-error" role="alert" className={`${styles.fieldError} body-sm-regular`}>
                {errors.lastName}
              </p>
            )}
          </div>

          <div className={styles.field}>
            <Input
              type="tel"
              name="phone"
              placeholder={t('phoneNumber')}
              aria-label={t('phoneNumber')}
              value={formData.phone}
              onChange={handleChange}
              error={submitted && !!errors.phone}
              autoComplete="tel"
              aria-describedby={submitted && errors.phone ? 'signup-phone-error' : undefined}
            />
            {submitted && errors.phone && (
              <p id="signup-phone-error" role="alert" className={`${styles.fieldError} body-sm-regular`}>
                {errors.phone}
              </p>
            )}
          </div>

          <div className={styles.field}>
            <PasswordInput
              name="password"
              placeholder={t('password')}
              aria-label={t('password')}
              value={formData.password}
              onChange={handleChange}
              error={submitted && !!errors.password}
              autoComplete="new-password"
              aria-describedby={submitted && errors.password ? 'signup-password-error' : undefined}
            />
            {submitted && errors.password && (
              <p id="signup-password-error" role="alert" className={`${styles.fieldError} body-sm-regular`}>
                {errors.password}
              </p>
            )}
          </div>

          {/*
            Consent text carries a link to the policy, so it cannot sit inside a <label> — a click
            on the link would toggle the checkbox as well. Mirror login remember-me: clicking the
            text toggles via onClick; the policy link stops propagation so it only opens. Wording
            is adapted for רכזים (SPR-1204): which data is collected, why, and third parties.
          */}
          <div className={styles.consentBlock}>
            <div className={`${styles.consentRow} ${submitted && errors.privacy ? styles.consentRowError : ''}`}>
              <Checkbox
                ref={privacyCheckboxRef}
                checked={privacyAcceptedAt !== null}
                onCheckedChange={(value) => {
                  setPrivacyAcceptedAt(value ? new Date().toISOString() : null);
                  if (value) {
                    setErrors((prev) => {
                      const next = { ...prev };
                      delete next.privacy;
                      return next;
                    });
                  }
                }}
                ariaLabelledBy="signup-privacy-consent-text"
                ariaDescribedBy={submitted && errors.privacy ? 'signup-privacy-consent-error' : undefined}
                ariaInvalid={submitted && !!errors.privacy}
                ariaRequired
              />
              <span
                id="signup-privacy-consent-text"
                className={styles.consentText}
                onClick={() => {
                  setPrivacyAcceptedAt((prev) => {
                    const next = prev ? null : new Date().toISOString();
                    if (next) {
                      setErrors((errs) => {
                        const copy = { ...errs };
                        delete copy.privacy;
                        return copy;
                      });
                    }
                    return next;
                  });
                }}
              >
                {t('signupPrivacyConsent.before')}
                <a
                  href={PRIVACY_POLICY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.consentLink}
                  onClick={(e) => e.stopPropagation()}
                >
                  {t('signupPrivacyConsent.linkText')}
                </a>
                {t('signupPrivacyConsent.after')}
              </span>
            </div>
            {submitted && errors.privacy && (
              <span id="signup-privacy-consent-error" role="alert" className={`body-sm-regular ${styles.consentError}`}>
                {errors.privacy}
              </span>
            )}
          </div>

          <div className={styles.buttonsContainer}>
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? (
                <>
                  <span className={styles.spinner}></span> {t('signed')}
                </>
              ) : (
                t('signup')
              )}
            </Button>

            <Link to="/login"> {t('login')} </Link>
          </div>
        </form>
      )}
    </div>
  );
};

export default SigninComponent;
